import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaMicrophone, FaSave, FaSpinner, FaUpload } from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EditShadowingTaskPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [level, setLevel] = useState("");
  const [taskNumber, setTaskNumber] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setLevel(data.level);
        setTaskNumber(data.taskNumber.toString());
        setTaskDescription(data.taskDescription || "");
        setCurrentAudioUrl(data.audioDownloadUrl);
      } else {
        setError("Failed to load task details");
      }
    } catch (err) {
      console.error("Error fetching task:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("Audio file must be less than 10MB");
        setAudioFile(null);
        return;
      }

      // Validate file type
      const validTypes = [
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "audio/ogg",
        "audio/mp4",
        "audio/m4a",
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(mp3|wav|ogg|m4a|mp4)$/i)
      ) {
        setError("Audio file must be MP3, WAV, OGG, or M4A format");
        setAudioFile(null);
        return;
      }

      setAudioFile(file);
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!level) {
      setError("Please select a level");
      return;
    }

    if (!taskNumber || parseInt(taskNumber) < 1 || parseInt(taskNumber) > 14) {
      setError("Task number must be between 1 and 14");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();

      const taskData = {
        level: level,
        taskNumber: parseInt(taskNumber),
        taskDescription: taskDescription.trim() || null,
      };

      formData.append(
        "taskData",
        new Blob([JSON.stringify(taskData)], { type: "application/json" })
      );

      // Only append audio if a new file is selected
      if (audioFile) {
        formData.append("audioFile", audioFile);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}`,
        {
          method: "PUT",
          credentials: "include",
          body: formData,
        }
      );

      if (response.ok) {
        navigate("/admin/shadowing-tasks");
      } else {
        const data = await response.json();
        setError(
          data.error ||
            "Failed to update shadowing task. Task number may already exist for this level."
        );
      }
    } catch (err) {
      console.error("Error updating task:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaMicrophone />}
        title="Edit Shadowing Task"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Shadowing Tasks", to: "/admin/shadowing-tasks" },
          { label: "Edit" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaMicrophone className="text-[#009fe3]" />
            Edit Shadowing Task
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Level Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                German Level *
              </label>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              >
                <option value="">Select Level</option>
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
              </select>
            </div>

            {/* Task Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Task Number (1-14) *
              </label>
              <input
                type="number"
                min="1"
                max="14"
                value={taskNumber}
                onChange={(e) => setTaskNumber(e.target.value)}
                placeholder="Enter task number (1-14)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Each level has 14 shadowing tasks. Enter a unique number between
                1 and 14.
              </p>
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Task Description (Optional)
              </label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="Optional: Add any instructions or context for this shadowing task..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all resize-none"
                style={{ fontSize: "16px" }}
              />
            </div>

            {/* Current Audio */}
            {currentAudioUrl && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Audio
                </label>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <audio controls className="w-full" src={currentAudioUrl}>
                    Your browser does not support audio playback.
                  </audio>
                  <p className="text-xs text-gray-600 mt-2">
                    Upload a new file below to replace this audio
                  </p>
                </div>
              </div>
            )}

            {/* Audio File Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Upload New Audio File (Optional - MP3, WAV, OGG, M4A - Max 10MB)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-[#009fe3] transition-colors">
                <div className="space-y-1 text-center">
                  <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="audio-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-[#009fe3] hover:text-cyan-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#009fe3]"
                    >
                      <span>Upload a new audio file</span>
                      <input
                        id="audio-upload"
                        name="audio-upload"
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/m4a"
                        onChange={handleAudioChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    MP3, WAV, OGG, M4A up to 10MB
                  </p>
                </div>
              </div>
              {audioFile && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    ✓ New File Selected: {audioFile.name} (
                    {(audioFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                  <audio
                    controls
                    className="w-full mt-2"
                    src={URL.createObjectURL(audioFile)}
                  >
                    Your browser does not support audio playback.
                  </audio>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/shadowing-tasks")}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditShadowingTaskPage;
